import type { ColumnDef } from "@tanstack/react-table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { renderEmptyCellState } from "@/components/Table/utils";
import { toFormat } from "@/lib/dateHelpers";
import { hasNestedAchievementFields, resolveSectionName } from "../_utils";
import type { AchievementReportRow, SectionLookup } from "../types";

type ExpandedAchievementDetailRow = {
  id: string;
  recognitionYear: string | null;
  reportingEntity: string | null;
  expiresOn: string | null;
  remarks: string | null;
};

const expandedAchievementColumns: ColumnDef<ExpandedAchievementDetailRow, unknown>[] = [
  {
    id: "recognitionYear",
    header: "Recognition Year",
    accessorKey: "recognitionYear",
    enableSorting: false,
    cell: ({ getValue }) => renderEmptyCellState(getValue())
  },
  {
    id: "reportingEntity",
    header: "Reporting Entity",
    accessorKey: "reportingEntity",
    enableSorting: false,
    cell: ({ getValue }) => renderEmptyCellState(getValue())
  },
  {
    id: "expiresOn",
    header: "Expires On",
    accessorKey: "expiresOn",
    enableSorting: false,
    cell: ({ getValue }) => renderEmptyCellState(getValue())
  },
  {
    id: "remarks",
    header: "Remarks",
    accessorKey: "remarks",
    enableSorting: false,
    cell: ({ getValue }) => renderEmptyCellState(getValue())
  }
];

export default function ExpandedAchievementFields({
  row,
  sectionLookup
}: {
  row: AchievementReportRow;
  sectionLookup: SectionLookup;
}) {
  if (!hasNestedAchievementFields(row)) {
    return null;
  }

  const data: ExpandedAchievementDetailRow[] = [
    {
      id: row.id,
      recognitionYear: row.recognitionYear ?? null,
      reportingEntity: resolveSectionName(row.reportingEntity, sectionLookup) ?? row.reportingEntity ?? null,
      expiresOn: row.expiresOn ? toFormat(row.expiresOn) || row.expiresOn : null,
      remarks: row.remarks ?? null
    }
  ];

  return (
    <div data-testid="expanded-achievement-fields">
      <TableDetailsPanel
        data={data}
        columns={expandedAchievementColumns}
        title={`Achievement Details (${data.length})`}
      />
    </div>
  );
}
